import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { X, Share2, FileDown, Receipt, BookUser, Boxes, CheckCircle2, Bell, Phone, Pencil, Ban, Trash2, PackagePlus, Check, RotateCcw, Truck, Users, Loader2 } from "lucide-react";
import { listInvoices, getKhata, settleBill, recordPayment, setKhataPhone, invoicePdfUrl, invoiceToWhatsappText, shareInvoicePdf, canShareFiles, editInvoice, cancelInvoice, listPurchases, editPurchase, deletePurchase, returnBill, getSupplierKhata, paySupplier, getCustomers, saveCustomer, deleteCustomer, getCustomerBills } from "@/lib/api";
import InventoryTab from "@/components/InventoryTab";

function inRange(iso, from, to) {
  if (!iso) return true;
  const d = String(iso).slice(0, 10);
  return (!from || d >= from) && (!to || d <= to);
}

function DateRangeFilter({ from, to, setFrom, setTo }) {
  return (
    <div className="flex items-center gap-1.5 bg-white rounded-xl border border-black/5 shadow-sm px-2 py-1.5">
      <input type="date" data-testid="filter-from-date" value={from} onChange={(e) => setFrom(e.target.value)}
        className="flex-1 min-w-0 text-[10px] font-mono text-gray-600 outline-none bg-transparent" />
      <span className="text-[9px] text-gray-400 font-bold">SE</span>
      <input type="date" data-testid="filter-to-date" value={to} onChange={(e) => setTo(e.target.value)}
        className="flex-1 min-w-0 text-[10px] font-mono text-gray-600 outline-none bg-transparent" />
      {(from || to) && (
        <button data-testid="filter-clear-btn" onClick={() => { setFrom(""); setTo(""); }}
          className="text-gray-400 hover:text-red-500"><X size={12} /></button>
      )}
    </div>
  );
}

function groupByDay(invoices) {
  const groups = {};
  invoices.forEach((inv) => {
    (groups[inv.date] = groups[inv.date] || []).push(inv);
  });
  return Object.entries(groups).map(([date, invs]) => ({
    date,
    invs,
    total: invs.filter((i) => i.status !== "cancelled").reduce((s, i) => s + Number(i.total || 0), 0),
  }));
}

function BillEditor({ inv, onDone, onCancelEdit }) {
  const gstOn = inv.gst_enabled !== false;
  const [items, setItems] = useState(inv.items.map((it) => ({ ...it })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const setField = (i, field, val) =>
    setItems((arr) => arr.map((it, j) => (j === i ? { ...it, [field]: val } : it)));
  const remove = (i) => setItems((arr) => arr.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await editInvoice(inv.id, {
        items: items.map((it) => ({
          name: it.name, qty: Number(it.qty) || 1, unit: it.unit || null,
          rate: Number(it.rate) || 0, gst_rate: gstOn ? Number(it.gst_rate) || 0 : 0,
        })),
      });
      onDone();
    } catch (e) {
      console.error("edit failed", e);
      setError(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save nahi hua");
      setSaving(false);
    }
  };

  return (
    <div data-testid="bill-editor" className="mt-2 border-t border-dashed border-gray-200 pt-2 space-y-1.5">
      {items.map((it, i) => (
        <div key={`row-${i}`} className="flex items-center gap-1.5">
          <input data-testid="bill-edit-name" value={it.name}
            onChange={(e) => setField(i, "name", e.target.value)}
            className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1 text-[11px] outline-none focus:ring-1 focus:ring-[#006D5B]" />
          <input data-testid="bill-edit-qty" value={it.qty} type="number" min="0" step="any" title="Qty"
            onChange={(e) => setField(i, "qty", e.target.value)}
            className="w-14 border border-gray-200 rounded-lg px-2 py-1 text-[11px] font-mono outline-none focus:ring-1 focus:ring-[#006D5B]" />
          <input data-testid="bill-edit-rate" value={it.rate} type="number" min="0" step="any" title="Rate ₹"
            onChange={(e) => setField(i, "rate", e.target.value)}
            className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-[11px] font-mono outline-none focus:ring-1 focus:ring-[#006D5B]" />
          {gstOn && (
            <span className="flex items-center gap-0.5">
              <input data-testid="bill-edit-gst" value={it.gst_rate ?? 0} type="number" min="0" max="28" step="any" title="GST %"
                onChange={(e) => setField(i, "gst_rate", e.target.value)}
                className="w-12 border border-amber-200 bg-amber-50/50 rounded-lg px-1.5 py-1 text-[11px] font-mono outline-none focus:ring-1 focus:ring-[#006D5B]" />
              <span className="text-[9px] text-gray-400">%</span>
            </span>
          )}
          {items.length > 1 && (
            <button data-testid="bill-edit-remove-item" onClick={() => remove(i)} title="Item hatao"
              className="text-gray-300 hover:text-red-500"><Trash2 size={13} /></button>
          )}
        </div>
      ))}
      {error && <p className="text-[10px] text-red-500">{String(error)}</p>}
      <div className="flex gap-2 pt-1">
        <button data-testid="bill-edit-save-btn" onClick={save} disabled={saving}
          className="flex-1 bg-[#006D5B] text-white text-[11px] font-bold py-1.5 rounded-full disabled:opacity-50">
          {saving ? "Saving..." : gstOn ? "Save (GST auto-recalc)" : "Save"}
        </button>
        <button data-testid="bill-edit-cancel-btn" onClick={onCancelEdit}
          className="text-[11px] text-gray-400 font-semibold px-3">Rehne do</button>
      </div>
    </div>
  );
}

function ReturnPanel({ inv, onDone, onClose }) {
  const [qtys, setQtys] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const save = async () => {
    const items = Object.entries(qtys)
      .filter(([, v]) => Number(v) > 0)
      .map(([name, v]) => ({ name, qty: Number(v) }));
    if (!items.length || saving) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await returnBill(inv.id, items);
      if (data.refund_due > 0) {
        setInfo(`✅ Return ho gaya — customer ko ₹${Number(data.refund_due).toFixed(2)} wapas dena hai`);
        setTimeout(onDone, 2200);
      } else {
        onDone();
      }
    } catch (e) {
      console.error("return failed", e);
      setError(e?.response?.data?.detail || "Return nahi hua");
      setSaving(false);
    }
  };

  if (info) return <p data-testid="bill-return-info" className="mt-2 text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-lg px-2.5 py-2">{info}</p>;

  return (
    <div data-testid="bill-return-panel" className="mt-2 border-t border-dashed border-amber-200 pt-2 space-y-1.5 bg-amber-50/40 -mx-1 px-1 rounded-b-xl">
      <p className="text-[10px] font-bold text-amber-700 flex items-center gap-1"><RotateCcw size={11} /> Kya wapas aaya? (stock bhi wapas judega)</p>
      {(inv.items || []).map((it) => (
        <div key={it.name} className="flex items-center gap-2 text-[11px]">
          <span className="flex-1 min-w-0 truncate font-semibold">{it.name}</span>
          <span className="text-gray-400 font-mono">{Number(it.qty)}{it.unit ? ` ${it.unit}` : ""} billed</span>
          <input data-testid="bill-return-qty" value={qtys[it.name] ?? ""} type="number" min="0" max={it.qty} step="any"
            onChange={(e) => setQtys((q) => ({ ...q, [it.name]: e.target.value }))}
            placeholder="0" className="w-14 border border-amber-200 rounded-lg px-2 py-1 text-[11px] font-mono bg-white outline-none focus:ring-1 focus:ring-amber-400" />
        </div>
      ))}
      {error && <p data-testid="bill-return-error" className="text-[10px] text-red-500">{String(error)}</p>}
      <div className="flex gap-2 pt-1 pb-1.5">
        <button data-testid="bill-return-save-btn" onClick={save} disabled={saving || !Object.values(qtys).some((v) => Number(v) > 0)}
          className="flex-1 bg-amber-500 text-white text-[11px] font-bold py-1.5 rounded-full disabled:opacity-40">
          {saving ? "Saving..." : "Return karo (bill adjust hoga)"}
        </button>
        <button onClick={onClose} className="text-[11px] text-gray-400 font-semibold px-3">Rehne do</button>
      </div>
    </div>
  );
}

function BillsTab() {
  const [invoices, setInvoices] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [returningId, setReturningId] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => {
    listInvoices()
      .then(({ data }) => setInvoices(data.invoices))
      .catch((e) => {
        console.error("invoice list failed", e);
        setInvoices([]);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = invoices ? invoices.filter((inv) => inRange(inv.created_at, from, to)) : null;
  const days = filtered ? groupByDay(filtered) : null;
  const rangeActive = from || to;
  const rangeTotal = filtered
    ? filtered.filter((i) => i.status !== "cancelled").reduce((s, i) => s + Number(i.total || 0), 0) : 0;
  const rangeCount = filtered ? filtered.filter((i) => i.status !== "cancelled").length : 0;

  const share = async (inv) => {
    if (canShareFiles()) {
      const shared = await shareInvoicePdf(inv);
      if (shared) return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(invoiceToWhatsappText(inv))}`, "_blank", "noopener");
  };

  const doCancel = async (inv) => {
    if (!window.confirm(`Bill ${inv.invoice_no} cancel karna hai? Hisab se hat jayega.`)) return;
    try {
      await cancelInvoice(inv.id);
      load();
    } catch (e) {
      console.error("cancel failed", e);
    }
  };

  const togglePay = async (inv) => {
    const next = inv.payment_status === "udhaar" ? "paid" : "udhaar";
    const label = next === "udhaar" ? "Udhaar (khata) me daalna hai?" : "Paid (cash) mark karna hai?";
    if (!window.confirm(`Bill ${inv.invoice_no} — ${label}`)) return;
    try {
      await editInvoice(inv.id, { payment_status: next });
      load();
    } catch (e) {
      console.error("paytype toggle failed", e);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto chat-scroll p-3 space-y-4">
      <DateRangeFilter from={from} to={to} setFrom={setFrom} setTo={setTo} />
      {rangeActive && filtered && (
        <div data-testid="bills-range-summary" className="bg-[#111827] text-white rounded-xl p-3 flex justify-between items-center -mt-1">
          <span className="text-xs font-semibold">{rangeCount} bill{rangeCount !== 1 ? "s" : ""} is range mein</span>
          <span className="font-mono font-bold">₹{rangeTotal.toFixed(2)}</span>
        </div>
      )}
      {days === null && <p className="text-sm text-gray-400 text-center mt-10">Loading...</p>}
      {days && days.length === 0 && (
        <p data-testid="invoice-history-empty" className="text-sm text-gray-500 text-center mt-10 px-6">
          {rangeActive ? "Is date range mein koi bill nahi mila." : "Abhi tak koi bill nahi bana. Chat mein items likho — bill yahan save hota jayega! 🧾"}
        </p>
      )}
      {days && days.map((day) => (
        <div key={day.date} data-testid="invoice-history-day">
          <div className="flex justify-between items-center mb-2 px-1">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">{day.date}</span>
            <span data-testid="invoice-history-day-total" className="text-[11px] font-bold bg-[#FFC107] text-[#111827] px-2 py-0.5 rounded-full">
              Total ₹{day.total.toFixed(2)}
            </span>
          </div>
          <div className="space-y-2">
            {day.invs.map((inv) => {
              const cancelled = inv.status === "cancelled";
              return (
                <div key={inv.id || inv.invoice_no} data-testid="invoice-history-item"
                  className={`bg-white rounded-xl border border-black/5 shadow-sm p-3 ${cancelled ? "opacity-60" : ""}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold text-[#006D5B] flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${cancelled ? "line-through" : ""}`}>
                        <span>{inv.invoice_no}</span>
                        {inv.payment_status === "udhaar" && !cancelled && (
                          <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold no-underline">UDHAAR</span>
                        )}
                        {cancelled && (
                          <span data-testid="invoice-cancelled-badge" className="text-[9px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full font-bold">CANCELLED</span>
                        )}
                        {inv.edited_at && !cancelled && (
                          <span data-testid="invoice-edited-badge" className="text-[9px] bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded-full font-bold">EDITED</span>
                        )}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {inv.customer} · {(inv.items || []).length} item{(inv.items || []).length > 1 ? "s" : ""}
                        {inv.payment_status === "udhaar" && Number(inv.paid_amount || 0) > 0 && (
                          <span data-testid="bill-partial-info" className="text-emerald-600 font-semibold"> · ₹{Number(inv.paid_amount).toFixed(2)} mila</span>
                        )}
                        {(inv.returns || []).length > 0 && (
                          <span data-testid="bill-returns-info" className="text-amber-600 font-semibold"> · 🔄 {(inv.returns || []).reduce((s, r) => s + Number(r.qty || 0), 0)} return</span>
                        )}
                      </p>
                    </div>
                    <span className={`shrink-0 text-sm font-bold font-mono ${cancelled ? "line-through text-gray-400" : ""}`}>₹{Number(inv.total).toFixed(2)}</span>
                    {!cancelled && (
                      <>
                        <button data-testid="invoice-paytype-btn" onClick={() => togglePay(inv)}
                          title={inv.payment_status === "udhaar" ? "Paid (cash) mark karo" : "Udhaar (khata) me badlo"}
                          className={`w-7 h-7 rounded-full border flex items-center justify-center transition-colors duration-150 ${
                            inv.payment_status === "udhaar"
                              ? "border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                              : "border-red-200 text-red-400 hover:bg-red-50"
                          }`}>
                          {inv.payment_status === "udhaar" ? <CheckCircle2 size={12} /> : <BookUser size={12} />}
                        </button>
                        <button data-testid="invoice-edit-btn" onClick={() => { setEditingId(editingId === inv.id ? null : inv.id); setReturningId(null); }} title="Bill edit karo"
                          className="w-7 h-7 rounded-full border border-gray-200 text-gray-500 flex items-center justify-center hover:border-[#006D5B] hover:text-[#006D5B] transition-colors duration-150">
                          <Pencil size={12} />
                        </button>
                        <button data-testid="invoice-return-btn" onClick={() => { setReturningId(returningId === inv.id ? null : inv.id); setEditingId(null); }} title="Kuch item wapas aaya? Return karo"
                          className="w-7 h-7 rounded-full border border-amber-300 text-amber-500 flex items-center justify-center hover:bg-amber-50 transition-colors duration-150">
                          <RotateCcw size={12} />
                        </button>
                        <button data-testid="invoice-cancel-btn" onClick={() => doCancel(inv)} title="Bill cancel karo"
                          className="w-7 h-7 rounded-full border border-gray-200 text-gray-500 flex items-center justify-center hover:border-red-400 hover:text-red-500 transition-colors duration-150">
                          <Ban size={12} />
                        </button>
                        <button data-testid="invoice-history-share-btn" onClick={() => share(inv)} title="WhatsApp share"
                          className="w-7 h-7 rounded-full bg-[#006D5B] text-white flex items-center justify-center hover:scale-105 transition-transform duration-150">
                          <Share2 size={12} />
                        </button>
                        {inv.id && (
                          <a data-testid="invoice-history-pdf-btn" href={invoicePdfUrl(inv.id)} target="_blank" rel="noopener noreferrer" title="PDF"
                            className="w-7 h-7 rounded-full border border-[#006D5B] text-[#006D5B] flex items-center justify-center hover:bg-[#006D5B]/5 transition-colors duration-150">
                            <FileDown size={12} />
                          </a>
                        )}
                      </>
                    )}
                  </div>
                  {editingId === inv.id && !cancelled && (
                    <BillEditor inv={inv} onDone={() => { setEditingId(null); load(); }} onCancelEdit={() => setEditingId(null)} />
                  )}
                  {returningId === inv.id && !cancelled && (
                    <ReturnPanel inv={inv} onDone={() => { setReturningId(null); load(); }} onClose={() => setReturningId(null)} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function KhataTab() {
  const [mode, setMode] = useState("customer");
  const pill = (active) =>
    `flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] font-bold rounded-full transition-colors duration-150 ${
      active ? "bg-[#006D5B] text-white" : "bg-white text-gray-500 border border-gray-200"}`;
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex gap-1.5 px-3 pt-2.5">
        <button data-testid="khata-mode-customer" onClick={() => setMode("customer")} className={pill(mode === "customer")}>
          <BookUser size={12} /> Lena hai
        </button>
        <button data-testid="khata-mode-supplier" onClick={() => setMode("supplier")} className={pill(mode === "supplier")}>
          <Truck size={12} /> Dena hai
        </button>
      </div>
      {mode === "customer" ? <CustomerKhata /> : <SupplierKhata />}
    </div>
  );
}

function SupplierKhata() {
  const [data, setData] = useState(null);
  const [payingSup, setPayingSup] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payError, setPayError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    getSupplierKhata()
      .then(({ data }) => setData(data))
      .catch((e) => { console.error("supplier khata failed", e); setData({ suppliers: [], total_pending: 0 }); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePayment = async (supplier) => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0 || saving) return;
    setSaving(true);
    setPayError("");
    try {
      await paySupplier(supplier, amt);
      setPayingSup(null);
      setPayAmount("");
      load();
    } catch (e) {
      setPayError(e?.response?.data?.detail || "Payment save nahi hua");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto chat-scroll p-3 space-y-3">
      {data === null && <p className="text-sm text-gray-400 text-center mt-10">Loading...</p>}
      {data && data.suppliers.length === 0 && (
        <p data-testid="supplier-khata-empty" className="text-sm text-gray-500 text-center mt-10 px-6">
          📕 Kisi supplier ko kuch nahi dena!
          <br /><br />
          <span className="text-[11px] text-gray-400">Chat mein bolo — "Sharma se 50 kilo chawal aaya 40 ke bhav, udhaar pe" — yahan track hoga.</span>
        </p>
      )}
      {data && data.suppliers.length > 0 && (
        <div data-testid="supplier-khata-total" className="bg-[#7C2D12] text-white rounded-xl p-3 flex justify-between items-center">
          <span className="text-xs font-semibold">Suppliers ko dena hai</span>
          <span className="font-mono font-bold">₹{data.total_pending.toFixed(2)}</span>
        </div>
      )}
      {data && data.suppliers.map((s) => (
        <div key={s.supplier} data-testid="supplier-khata-card" className="bg-white rounded-xl border border-black/5 shadow-sm p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate flex items-center gap-1.5"><Truck size={13} className="text-gray-400" /> {s.supplier}</p>
              <p className="text-[11px] text-gray-500">{s.entries.length} udhaar entr{s.entries.length > 1 ? "ies" : "y"}</p>
            </div>
            <span data-testid="supplier-pending" className="text-sm font-bold font-mono text-red-600">₹{s.pending.toFixed(2)}</span>
            <button data-testid="supplier-pay-btn" onClick={() => { setPayingSup(payingSup === s.supplier ? null : s.supplier); setPayAmount(""); setPayError(""); }}
              className="flex items-center gap-0.5 border border-[#006D5B]/40 text-[#006D5B] text-[10px] font-bold px-2.5 py-1.5 rounded-full hover:bg-[#006D5B]/5">
              + ₹ Paid
            </button>
          </div>
          {payingSup === s.supplier && (
            <div className="mb-2">
              <div className="flex items-center gap-1.5">
                <input data-testid="supplier-pay-input" value={payAmount} type="number" min="1" step="any"
                  onChange={(e) => setPayAmount(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && savePayment(s.supplier)}
                  placeholder={`Kitna diya? (max ₹${s.pending.toFixed(0)})`}
                  className="flex-1 min-w-0 border border-gray-200 rounded-full px-2.5 py-1 text-[11px] font-mono outline-none focus:ring-1 focus:ring-[#006D5B]" />
                <button data-testid="supplier-pay-save-btn" onClick={() => savePayment(s.supplier)} disabled={saving}
                  className="bg-[#006D5B] text-white text-[10px] font-bold px-3 py-1 rounded-full disabled:opacity-50">{saving ? "..." : "Save"}</button>
              </div>
              {payError && <p data-testid="supplier-pay-error" className="text-[10px] text-red-500 mt-0.5">{String(payError)}</p>}
            </div>
          )}
          <div className="space-y-1 border-t border-dotted border-gray-200 pt-2">
            {s.entries.map((e) => (
              <div key={e.id} data-testid="supplier-entry" className="flex items-center gap-2 text-[11px]">
                <span className="flex-1 min-w-0 truncate text-gray-600">{e.name} · {Number(e.qty)}{e.unit ? ` ${e.unit}` : ""} <span className="text-gray-400">({e.date})</span></span>
                <span className="font-mono font-semibold">
                  ₹{e.remaining.toFixed(2)}
                  {e.paid_amount > 0 && <span className="block text-[9px] font-normal text-emerald-600">₹{e.paid_amount.toFixed(2)} diya</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomerKhata() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [payingId, setPayingId] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payError, setPayError] = useState("");
  const [paySaving, setPaySaving] = useState(false);

  const load = useCallback(() => {
    getKhata()
      .then(({ data }) => setData(data))
      .catch((e) => {
        console.error("khata load failed", e);
        setData({ customers: [], total_pending: 0 });
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const settle = async (invoiceId) => {
    try {
      await settleBill(invoiceId);
      load();
    } catch (e) {
      console.error("settle failed", e);
    }
  };

  const savePayment = async (invoiceId) => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0 || paySaving) return;
    setPayError("");
    setPaySaving(true);
    try {
      await recordPayment(invoiceId, amt);
      setPayingId(null);
      setPayAmount("");
      load();
    } catch (e) {
      console.error("payment failed", e);
      setPayError(e?.response?.data?.detail || "Payment save nahi hua");
    } finally {
      setPaySaving(false);
    }
  };

  const savePhone = async (customer) => {
    if (!phoneInput.trim()) return;
    try {
      await setKhataPhone(customer, phoneInput.trim());
      setEditing(null);
      setPhoneInput("");
      load();
    } catch (e) {
      console.error("phone save failed", e);
    }
  };

  const remind = (c) => {
    const text = `Namaste ${c.customer} ji 🙏\n\nAapka ₹${c.pending.toFixed(2)} ka udhaar pending hai (${c.bills.length} bill${c.bills.length > 1 ? "s" : ""}). Jab suvidha ho, payment kar dena. Dhanyawad! 🙏`;
    const target = c.phone ? `https://wa.me/${c.phone}?text=` : "https://wa.me/?text=";
    window.open(target + encodeURIComponent(text), "_blank", "noopener");
  };

  return (
    <div className="flex-1 overflow-y-auto chat-scroll p-3 space-y-3">
      {data === null && <p className="text-sm text-gray-400 text-center mt-10">Loading...</p>}
      {data && data.customers.length === 0 && (
        <p data-testid="khata-empty" className="text-sm text-gray-500 text-center mt-10 px-6">
          📗 Khata bilkul clear hai! Koi udhaar pending nahi.
          <br /><br />
          <span className="text-[11px] text-gray-400">Bill banate waqt "udhaar" bol do — yahan track hoga.</span>
        </p>
      )}
      {data && data.customers.length > 0 && (
        <div data-testid="khata-total" className="bg-[#111827] text-white rounded-xl p-3 flex justify-between items-center">
          <span className="text-xs font-semibold">Total udhaar pending</span>
          <span className="font-mono font-bold">₹{data.total_pending.toFixed(2)}</span>
        </div>
      )}
      {data && data.customers.map((c) => (
        <div key={c.customer} data-testid="khata-customer" className="bg-white rounded-xl border border-black/5 shadow-sm p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{c.customer}</p>
              <p className="text-[11px] text-gray-500">{c.bills.length} bill{c.bills.length > 1 ? "s" : ""} pending</p>
            </div>
            <span data-testid="khata-customer-pending" className="text-sm font-bold font-mono text-red-600">₹{c.pending.toFixed(2)}</span>
            <button data-testid="khata-remind-btn" onClick={() => remind(c)} title={c.phone ? `Reminder seedha ${c.customer} ko` : "WhatsApp reminder bhejo"}
              className="flex items-center gap-1 bg-[#FFC107] text-[#111827] text-[10px] font-bold px-2.5 py-1.5 rounded-full hover:scale-105 transition-transform duration-150">
              <Bell size={11} /> Remind
            </button>
          </div>
          <div className="flex items-center gap-1.5 mb-2 text-[11px]">
            <Phone size={11} className="text-gray-400 shrink-0" />
            {editing === c.customer ? (
              <span className="flex items-center gap-1.5 flex-1">
                <input
                  data-testid="khata-phone-input"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && savePhone(c.customer)}
                  placeholder="10-digit number"
                  className="flex-1 min-w-0 border border-gray-200 rounded-full px-2.5 py-1 text-[11px] outline-none focus:ring-1 focus:ring-[#006D5B]"
                />
                <button data-testid="khata-phone-save-btn" onClick={() => savePhone(c.customer)}
                  className="text-[#006D5B] font-bold hover:underline">Save</button>
              </span>
            ) : c.phone ? (
              <span className="flex items-center gap-2">
                <span data-testid="khata-customer-phone" className="font-mono text-gray-600">+{c.phone}</span>
                <button data-testid="khata-phone-edit-btn" onClick={() => { setEditing(c.customer); setPhoneInput(""); }}
                  className="text-gray-400 hover:text-[#006D5B] font-semibold">edit</button>
              </span>
            ) : (
              <button data-testid="khata-phone-add-btn" onClick={() => { setEditing(c.customer); setPhoneInput(""); }}
                className="text-[#006D5B] font-semibold hover:underline">+ Number add karo (seedha reminder ke liye)</button>
            )}
          </div>
          <div className="space-y-1.5 border-t border-dotted border-gray-200 pt-2">
            {c.bills.map((b) => (
              <div key={b.id}>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="font-mono text-gray-500">{b.invoice_no} · {b.date}</span>
                  <span className="flex-1 text-right font-mono font-semibold">
                    ₹{Number(b.remaining ?? b.total).toFixed(2)}
                    {Number(b.paid_amount || 0) > 0 && (
                      <span data-testid="khata-partial-info" className="block text-[9px] font-normal text-emerald-600">
                        ₹{Number(b.paid_amount).toFixed(2)} mila · ₹{Number(b.total).toFixed(2)} ka bill
                      </span>
                    )}
                  </span>
                  <button data-testid="khata-payment-btn" onClick={() => { setPayingId(payingId === b.id ? null : b.id); setPayAmount(""); setPayError(""); }}
                    title="Kuch payment mili? Record karo"
                    className="flex items-center gap-0.5 border border-[#006D5B]/40 text-[#006D5B] font-bold px-2 py-0.5 rounded-full hover:bg-[#006D5B]/5">
                    + ₹
                  </button>
                  <button data-testid="khata-settle-btn" onClick={() => settle(b.id)} title="Poora paid mark karo"
                    className="flex items-center gap-1 text-[#006D5B] font-bold hover:underline">
                    <CheckCircle2 size={12} /> Settle
                  </button>
                </div>
                {payingId === b.id && (
                  <div className="flex items-center gap-1.5 mt-1.5 mb-0.5">
                    <input
                      data-testid="khata-payment-input"
                      value={payAmount}
                      type="number" min="1" step="any"
                      onChange={(e) => setPayAmount(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && savePayment(b.id)}
                      placeholder={`Kitna mila? (max ₹${Number(b.remaining ?? b.total).toFixed(0)})`}
                      className="flex-1 min-w-0 border border-gray-200 rounded-full px-2.5 py-1 text-[11px] font-mono outline-none focus:ring-1 focus:ring-[#006D5B]"
                    />
                    <button data-testid="khata-payment-save-btn" onClick={() => savePayment(b.id)} disabled={paySaving}
                      className="bg-[#006D5B] text-white text-[10px] font-bold px-3 py-1 rounded-full disabled:opacity-50">{paySaving ? "..." : "Save"}</button>
                  </div>
                )}
                {payingId === b.id && payError && (
                  <p data-testid="khata-payment-error" className="text-[10px] text-red-500 mt-0.5">{String(payError)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PurchasesTab() {
  const [rows, setRows] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [eQty, setEQty] = useState("");
  const [eCost, setECost] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listPurchases()
      .then(({ data }) => setRows(data.purchases))
      .catch((e) => { console.error("purchases load failed", e); setRows([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows ? rows.filter((r) => inRange(r.at, from, to)) : null;
  const buys = filtered ? filtered.filter((r) => r.kind !== "sales_return") : [];
  const returns = filtered ? filtered.filter((r) => r.kind === "sales_return") : [];
  const spend = buys.reduce((s, r) => s + (r.cost_price ? Number(r.qty) * Number(r.cost_price) : 0), 0);

  const saveEdit = async (r) => {
    const qty = Number(eQty);
    if (!qty || qty <= 0 || busy) return;
    setBusy(true);
    try {
      await editPurchase(r.id, { qty, cost_price: eCost !== "" ? Number(eCost) : null });
      setEditingId(null);
      load();
    } catch (e) { console.error("purchase edit failed", e); } finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`${r.name} (${r.qty}${r.unit ? " " + r.unit : ""}) ki purchase entry delete karein? Stock bhi utna kam ho jayega.`)) return;
    try {
      await deletePurchase(r.id);
      load();
    } catch (e) { console.error("purchase delete failed", e); }
  };

  return (
    <div className="flex-1 overflow-y-auto chat-scroll p-3 space-y-3">
      <DateRangeFilter from={from} to={to} setFrom={setFrom} setTo={setTo} />
      {filtered && filtered.length > 0 && (
        <div data-testid="purchases-summary" className="bg-[#111827] text-white rounded-xl p-3 flex justify-between items-center">
          <span className="text-xs font-semibold">
            {buys.length} purchase{buys.length !== 1 ? "s" : ""}{returns.length > 0 ? ` · ${returns.length} return${returns.length !== 1 ? "s" : ""}` : ""} · kharcha
          </span>
          <span className="font-mono font-bold">₹{spend.toFixed(2)}</span>
        </div>
      )}
      {rows === null && <p className="text-sm text-gray-400 text-center mt-10">Loading...</p>}
      {filtered && filtered.length === 0 && (
        <p data-testid="purchases-empty" className="text-sm text-gray-500 text-center mt-10 px-6">
          {from || to ? "Is date range mein koi entry nahi." : "Abhi tak koi maal entry nahi. Stock tab mein stock badhao ya chat mein bolo — \"50 kilo chawal aaya\" — har aane wala maal yahan record hoga! 📦"}
        </p>
      )}
      {filtered && filtered.map((r) => (
        <div key={r.id} data-testid="purchase-item" className="bg-white rounded-xl border border-black/5 shadow-sm p-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold truncate">
                {r.name}
                {r.kind === "sales_return" && (
                  <span data-testid="purchase-return-badge" className="ml-1.5 text-[8px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full align-middle">RETURN</span>
                )}
                {r.payment_status === "udhaar" && (
                  <span data-testid="purchase-udhaar-badge" className="ml-1.5 text-[8px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full align-middle">UDHAAR</span>
                )}
              </p>
              <p className="text-[10px] text-gray-500">
                {r.date}{r.supplier ? ` · ${r.supplier} se` : ""}{r.cost_price ? ` · cost ₹${Number(r.cost_price).toFixed(2)}${r.unit ? "/" + r.unit : ""}` : ""}{r.kind === "sales_return" ? (r.source === "bill_return" ? " · bill se return" : " · customer se wapas") : ""}
              </p>
            </div>
            <span className={`text-sm font-bold font-mono ${r.kind === "sales_return" ? "text-amber-600" : "text-[#006D5B]"}`}>+{Number(r.qty)}{r.unit ? ` ${r.unit}` : ""}</span>
            <button data-testid="purchase-edit-btn" title="Entry edit karo (stock adjust hoga)"
              onClick={() => { setEditingId(editingId === r.id ? null : r.id); setEQty(String(r.qty)); setECost(r.cost_price != null ? String(r.cost_price) : ""); }}
              className="w-7 h-7 rounded-full border border-gray-200 text-gray-500 flex items-center justify-center hover:border-[#006D5B] hover:text-[#006D5B]">
              <Pencil size={12} />
            </button>
            <button data-testid="purchase-delete-btn" title="Entry delete karo (stock bhi kam hoga)" onClick={() => remove(r)}
              className="w-7 h-7 rounded-full border border-gray-200 text-gray-400 flex items-center justify-center hover:border-red-400 hover:text-red-500">
              <Trash2 size={12} />
            </button>
          </div>
          {editingId === r.id && (
            <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed border-gray-200">
              <input data-testid="purchase-edit-qty" value={eQty} onChange={(e) => setEQty(e.target.value)}
                type="number" min="0" step="any" placeholder="qty"
                className="w-16 border border-gray-200 rounded-full px-2.5 py-1 text-[11px] font-mono outline-none focus:ring-1 focus:ring-[#006D5B]" />
              <input data-testid="purchase-edit-cost" value={eCost} onChange={(e) => setECost(e.target.value)}
                type="number" min="0" step="any" placeholder="cost ₹"
                className="w-16 border border-gray-200 rounded-full px-2.5 py-1 text-[11px] font-mono outline-none focus:ring-1 focus:ring-[#006D5B]" />
              <button data-testid="purchase-edit-save" onClick={() => saveEdit(r)} disabled={busy || !eQty}
                className="flex items-center gap-1 bg-[#006D5B] text-white text-[10px] font-bold px-3 py-1 rounded-full disabled:opacity-40">
                <Check size={11} /> Save
              </button>
              <span className="text-[9px] text-gray-400">stock bhi adjust hoga</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CustomerBills({ customer }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    getCustomerBills(customer).then(({ data }) => setData(data)).catch(() => setData({ bills: [], bill_count: 0, total_business: 0, pending: 0 }));
  }, [customer]);

  if (!data) return <p className="text-[11px] text-gray-400 py-2 text-center">Loading...</p>;
  return (
    <div data-testid="customer-bills-panel" className="bg-[#FDFBF7] rounded-xl border border-gray-100 p-2.5 space-y-2">
      <div className="flex gap-2 text-center">
        <div className="flex-1 bg-white rounded-lg border border-black/5 py-1.5">
          <p className="text-[13px] font-bold text-gray-800">{data.bill_count}</p>
          <p className="text-[9px] text-gray-400 uppercase font-bold">Bills</p>
        </div>
        <div className="flex-1 bg-white rounded-lg border border-black/5 py-1.5">
          <p className="text-[13px] font-bold text-[#006D5B]">₹{Math.round(data.total_business)}</p>
          <p className="text-[9px] text-gray-400 uppercase font-bold">Total business</p>
        </div>
        <div className="flex-1 bg-white rounded-lg border border-black/5 py-1.5">
          <p className={`text-[13px] font-bold ${data.pending > 0 ? "text-red-500" : "text-gray-800"}`}>₹{Math.round(data.pending)}</p>
          <p className="text-[9px] text-gray-400 uppercase font-bold">Udhaar pending</p>
        </div>
      </div>
      {data.bills.length === 0 ? (
        <p data-testid="customer-bills-empty" className="text-[11px] text-gray-400 text-center py-1">Abhi koi bill nahi bana is customer ka</p>
      ) : (
        <div className="space-y-1 max-h-48 overflow-y-auto chat-scroll">
          {data.bills.map((b) => (
            <div key={b.id} data-testid="customer-bill-row"
              className={`flex items-center gap-2 bg-white rounded-lg border border-black/5 px-2.5 py-1.5 ${b.status === "cancelled" ? "opacity-50" : ""}`}>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-gray-700 font-mono">{b.invoice_no}</p>
                <p className="text-[9px] text-gray-400">{b.date}</p>
              </div>
              <p className="text-[12px] font-bold text-gray-800 font-mono">₹{Number(b.total || 0).toFixed(0)}</p>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                b.status === "cancelled" ? "bg-gray-100 text-gray-400"
                  : b.payment_status === "udhaar" ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-600"}`}>
                {b.status === "cancelled" ? "Cancelled" : b.payment_status === "udhaar" ? "Udhaar" : "Paid"}
              </span>
              <a data-testid="customer-bill-pdf" href={invoicePdfUrl(b.id)} target="_blank" rel="noreferrer"
                className="text-gray-300 hover:text-[#006D5B]" title="PDF"><FileDown size={13} /></a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomersTab() {
  const [list, setList] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    getCustomers().then(({ data }) => setList(data.customers)).catch(() => setList([]));
  }, []);
  useEffect(load, [load]);

  const add = async () => {
    setBusy(true);
    setErr("");
    try {
      await saveCustomer(name.trim(), phone.trim());
      setName("");
      setPhone("");
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save nahi hua, dubara try karo");
    }
    setBusy(false);
  };

  const del = async (c) => {
    await deleteCustomer(c);
    load();
  };

  return (
    <div className="flex-1 overflow-y-auto chat-scroll p-3 space-y-3">
      <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-3 space-y-2">
        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Naya customer register karo</p>
        <p className="text-[10px] text-gray-400">Naam + WhatsApp number save karo — phir sirf naam bologe (&quot;Ramesh ko bill banao&quot;) aur bill unke WhatsApp pe apne aap chala jayega 📤</p>
        <div className="flex gap-2">
          <input data-testid="customer-name-input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Customer ka naam" className="flex-1 min-w-0 border border-gray-200 rounded-full px-3.5 py-2 text-[12px] outline-none focus:ring-2 focus:ring-[#006D5B]/40" />
          <input data-testid="customer-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="10-digit number" className="flex-1 min-w-0 border border-gray-200 rounded-full px-3.5 py-2 text-[12px] font-mono outline-none focus:ring-2 focus:ring-[#006D5B]/40" />
          <button data-testid="customer-save-btn" onClick={add} disabled={busy || !name.trim() || phone.trim().length < 10}
            className="bg-[#006D5B] text-white text-[11px] font-bold px-4 py-2 rounded-full disabled:opacity-50 shrink-0">
            {busy ? <Loader2 size={13} className="animate-spin" /> : "Save"}
          </button>
        </div>
        {err && <p data-testid="customer-error" className="text-[11px] text-red-500">{err}</p>}
      </div>
      {list === null ? (
        <p className="text-sm text-gray-400 text-center mt-6">Loading...</p>
      ) : list.length === 0 ? (
        <p data-testid="customers-empty" className="text-sm text-gray-400 text-center mt-6">Abhi koi customer registered nahi — upar se add karo!</p>
      ) : (
        <div data-testid="customers-list" className="bg-white rounded-2xl border border-black/5 shadow-sm divide-y divide-gray-50">
          {list.map((c) => (
            <div key={c.customer} className="px-3 py-2">
              <div data-testid="customer-row" className="flex items-center gap-3 cursor-pointer"
                onClick={() => setOpen(open === c.customer ? null : c.customer)}>
                <div className="w-8 h-8 rounded-full bg-[#006D5B]/10 text-[#006D5B] flex items-center justify-center text-xs font-bold shrink-0">
                  {c.customer.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-gray-800 truncate">{c.customer}</p>
                  <p className="text-[11px] text-gray-400 font-mono">{c.phone ? `+${c.phone}` : "—"}</p>
                </div>
                <span className="text-[9px] text-gray-400 font-semibold">{open === c.customer ? "band karo ▲" : "bills dekho ▼"}</span>
                <button data-testid="customer-delete-btn" onClick={(e) => { e.stopPropagation(); del(c.customer); }} title="Hatao"
                  className="text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
              {open === c.customer && <div className="mt-2"><CustomerBills customer={c.customer} /></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InvoiceHistory({ onClose }) {
  const [tab, setTab] = useState("bills");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      data-testid="invoice-history-panel"
      className="absolute inset-0 z-30 bg-[#FDFBF7] flex flex-col"
    >
      <div className="bg-[#006D5B] text-white px-4 py-3 flex items-center gap-3">
        <Receipt size={18} />
        <p className="font-semibold text-sm flex-1">Dukaan Register</p>
        <button data-testid="invoice-history-close-btn" onClick={onClose}
          className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors duration-150">
          <X size={17} />
        </button>
      </div>
      <div className="flex bg-white border-b border-black/5">
        <button
          data-testid="history-tab-bills"
          onClick={() => setTab("bills")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-colors duration-150 ${
            tab === "bills" ? "text-[#006D5B] border-b-2 border-[#006D5B]" : "text-gray-400"
          }`}
        >
          <Receipt size={13} /> Bills
        </button>
        <button
          data-testid="history-tab-khata"
          onClick={() => setTab("khata")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-colors duration-150 ${
            tab === "khata" ? "text-[#006D5B] border-b-2 border-[#006D5B]" : "text-gray-400"
          }`}
        >
          <BookUser size={13} /> Khata
        </button>
        <button
          data-testid="history-tab-stock"
          onClick={() => setTab("stock")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-colors duration-150 ${
            tab === "stock" ? "text-[#006D5B] border-b-2 border-[#006D5B]" : "text-gray-400"
          }`}
        >
          <Boxes size={13} /> Stock
        </button>
        <button
          data-testid="history-tab-purchases"
          onClick={() => setTab("purchases")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-colors duration-150 ${
            tab === "purchases" ? "text-[#006D5B] border-b-2 border-[#006D5B]" : "text-gray-400"
          }`}
        >
          <PackagePlus size={13} /> Maal
        </button>
        <button
          data-testid="history-tab-customers"
          onClick={() => setTab("customers")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-colors duration-150 ${
            tab === "customers" ? "text-[#006D5B] border-b-2 border-[#006D5B]" : "text-gray-400"
          }`}
        >
          <Users size={13} /> Grahak
        </button>
      </div>
      {tab === "bills" ? <BillsTab /> : tab === "khata" ? <KhataTab /> : tab === "purchases" ? <PurchasesTab /> : tab === "customers" ? <CustomersTab /> : <div className="flex-1 overflow-y-auto p-3"><InventoryTab /></div>}
    </motion.div>
  );
}
